import React, { useState, useEffect, useCallback, useRef } from "react";
import { F, CLSNAME } from "../data/aircraft";
import { STATS } from "../data/stats";
import { seatsTxt } from "../lib/engine";
import { Picture, Price } from "../lib/icons";

export default function FleetCarousel({ items, onSelect }) {
  const [active, setActive] = useState(0);
  const touch = useRef(null);

  useEffect(() => { setActive(0); }, [items]);

  const len = items.length;
  const go = useCallback((n) => setActive((a) => Math.max(0, Math.min(len - 1, n))), [len]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(active + 1);
      else if (e.key === "ArrowLeft") go(active - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, go]);

  if (!len) return null;
  const code = items[active];
  const f = F[code];

  // geometry per offset from the centered item
  const geom = (off) => {
    const a = Math.abs(off);
    const sign = Math.sign(off);
    const x = sign * (150 + (a - 1) * 150) + (a === 0 ? 0 : 0); // base spacing
    const map = {
      0: { x: 0, scale: 1, op: 1, z: 50, rot: 0 },
      1: { x: sign * 320, scale: 0.64, op: 0.6, z: 40, rot: sign * -14 },
      2: { x: sign * 520, scale: 0.44, op: 0.34, z: 30, rot: sign * -20 },
      3: { x: sign * 672, scale: 0.32, op: 0.16, z: 20, rot: sign * -24 },
    };
    return map[a] || { x: sign * 760, scale: 0.26, op: 0, z: 10, rot: sign * -26 };
  };

  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touch.current == null) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    if (dx < -40) go(active + 1);
    else if (dx > 40) go(active - 1);
    touch.current = null;
  };

  return (
    <div className="cf">
      <div className="cf-stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="cf-floor" aria-hidden="true" />
        {items.map((fk, i) => {
          const off = i - active;
          if (Math.abs(off) > 4) return null;
          const g = geom(off);
          const isCenter = off === 0;
          return (
            <div
              key={fk}
              className={"cf-item" + (isCenter ? " is-center" : "")}
              style={{ transform: `translateX(${g.x}px) scale(${g.scale})`, opacity: g.op, zIndex: g.z, pointerEvents: g.op ? "auto" : "none" }}
              onClick={() => (isCenter ? onSelect(fk) : go(i))}
              role="button"
              aria-label={F[fk].n}
            >
              <div className="cf-pic">
                <Picture code={fk} />
                {isCenter ? <span className="fit">{seatsTxt(fk)}</span> : null}
              </div>
            </div>
          );
        })}

        <button className="cnav prev" type="button" aria-label="Previous aircraft" disabled={active === 0} onClick={() => go(active - 1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
        </button>
        <button className="cnav next" type="button" aria-label="Next aircraft" disabled={active === len - 1} onClick={() => go(active + 1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
        </button>
      </div>

      <div className="cf-card">
        <div className="cf-card-top">
          <span className="cf-tag">{CLSNAME[f.c]}</span>
          <Price tier={f.t} />
        </div>
        <div className="cf-name">{f.n}</div>
        <div className="cf-specs">
          <span>{seatsTxt(code)}</span>
          {f.k !== "balloon" ? <><span className="dot">·</span><span>{f.r.toLocaleString("en-IN")} nm range</span><span className="dot">·</span><span>{f.s} kt</span></> : null}
          <span className="dot">·</span><span>{(STATS[code] || {}).n} in India</span>
        </div>
        <button className="cf-explore" type="button" onClick={() => onSelect(code)}>View details &amp; request</button>
        <div className="cf-progress"><span style={{ width: `${((active + 1) / len) * 100}%` }} /></div>
        <div className="cf-count">{active + 1} / {len}</div>
      </div>
    </div>
  );
}
